import {clamp, json, MiniSignal, objectToArray, performNextTick, random, shuffleArray} from "p3";
import common from "../common";
import definitions from "../definitions";
import ActorFactory from "../game/actor/ActorFactory";
import EnemyFactory from "../game/actor/enemy/EnemyFactory";
import Inventory from "../game/actor/item/Inventory";
import ItemFactory from "../game/actor/item/ItemFactory";
import CoinAnimation from "../game/actor/misc/CoinAnimation";
import NPC from "../game/actor/npc/NPC";
import NPCBatwitch from "../game/actor/npc/NPCBatWitch";
import NPCFactory from "../game/actor/npc/NPCFactory";
import NPCGiantChick from "../game/actor/npc/NPCGiantChick";
import NPCGiantSpider from "../game/actor/npc/NPCGiantSpider";
import NPCSuzie from "../game/actor/npc/NPCSuzie";
import PlayerBert from "../game/actor/player/PlayerBert";
import PlayerIvandoe from "../game/actor/player/PlayerIvandoe";
import QuestBossFight from "../game/quest/QuestBossFight";
import QuestDummy from "../game/quest/QuestDummy";
import QuestFactory from "../game/quest/QuestFactory";
import QuestFetch from "../game/quest/QuestFetch";
import QuestKill from "../game/quest/QuestKill";
import QuestManager from "../game/quest/QuestManager";
import QuestMinigame from "../game/quest/QuestMinigame";
import Tilemap from "../game/Tilemap";
import World from "../game/World";
import settings from "../settings";
import HUD from "../ui/HUD";
import IdleMessage from "../ui/idlemessage/IdleMessage";
import LocationTitle from "../ui/locationtitle/LocationTitle";
import TextFormat from "../ui/TextFormat";
import { locale, resizeLabelToFitText } from "../utils";
import Scene from "./Scene";

export default class GameScene extends Scene {
    constructor() {
        super("game");

        this._loadedLevel = "";
        this._levelLoaded = false;
        this._levelShortcuts = {};
        this._levelSpawns = {};
        this._levelShortcutFrom = definitions.LEVEL_SHORTCUT.DEFAULT;

        this._world = null;
        this._actors = [];
        this._npcs = [];
        this._items = [];
        this._enemies = [];

        this._interactions = [];

        this._hud = null;
        this._inventory = null;
        this._questManager = null;

        this._ivandoe = null;
        this._bert = null;

        this._collectNearbyItemRange = 60;

        this._cameraTarget = new PIXI.Point();
        this._cameraTargetEase = 2;
        this._cameraZoomInitial = 0.92;
        this._cameraZoomTarget = 1;
        this._cameraZoomTargetEase = 1;

        this._playerStart = new PIXI.Point();
        this._playerHealth = 0;
        this._playerHearts = 0;
        this._playerHeartsMin = 3;
        this._playerHeartsMax = 8;

        this._activeMinigame = null;
        this._activeMinigameTime = 0;
        this._activeMinigameDuration = 0;

        this._activeBossFight = null;

        this._debugDeleteSlot = false;
        this._debugDeleteSlotTime = 0;

        this._initialTutorialPosition = null;
        this._tutorialControlsHidden = false;

        this._incidentalMusic = null;
        this._incidentalMusicList = [];
        this._incidentalMusicTrack = 0;
        this._incidentalMusicDelay = { current: 0, max: 2 };

        this._ambientSounds = [];

        this._randomSFX = [];
        this._randomSFXTimer = {active:false, current:0, limit:0, limitRange:[8, 16]};

        this._joystickTouch = null;

        this._idleTime = {current:0, maxTime:120};
        this._idleMessage = null;

        this.signals.boss = new MiniSignal();
        this.signals.map = new MiniSignal();
        this.signals.quest = new MiniSignal();
        this.signals.inventory = new MiniSignal();
        this.signals.shop = new MiniSignal();
        this.signals.minigame = new MiniSignal();
        this.signals.outro = new MiniSignal();
    }

    init() {
        super.init();
    }

    loadScene() {
        const lastLocation = common.saveData.activeSlot.player.lastLocation;
        const levelToLoad = lastLocation ? lastLocation : definitions.DEFAULT_LEVEL_ID;
        this.loadLevel(levelToLoad).then(() => this.onSceneLoaded());
    }

    onSceneLoaded() {
        super.onSceneLoaded();

        this._world = new World();
        this._world.filterEntity = (gid, data, flippedHorizontally, flippedVertically) => this.filterEntity(gid, data, flippedHorizontally, flippedVertically);
        this.addChild(this._world);

        this._hud = new HUD();
        this.addChild(this._hud);

        this._inventory = new Inventory();
        this._inventory.loadFromActiveSlot();

        this._questManager = new QuestManager();
        this._questManager.signals.init.add(this.onQuestInit, this);
        this._questManager.signals.progress.add(this.onQuestProgress, this);
        this._questManager.signals.complete.add(this.onQuestComplete, this);
        this._questManager.signals.unassign.add(this.onQuestUnassign, this);
        this._questManager.loadFromActiveSlot();

        const hearts = common.saveData.activeSlot.player.hearts;
        if (hearts) {
            this.setPlayerHearts(hearts);
        } else {
            this.setPlayerHearts(this._playerHearts = this._playerHeartsMin);
        }

        this.signalListeners.push(
            P3.input.signals.mouseDown.add(this.onMouseDown, this),
            P3.input.signals.mouseUp.add(this.onMouseUp, this),
            P3.input.signals.mouseMove.add(this.onMouseMove, this),

            this._hud.dialogSystem.signals.begin.add(this.onDialogBegin, this),
            this._hud.dialogSystem.signals.next.add(this.onDialogNext, this),
            this._hud.dialogSystem.signals.end.add(this.onDialogEnd, this),
            this._hud.dialogSystem.signals.introduce.add(this.onDialogIntroduceNPC, this),
            this._hud.dialogSystem.signals.assign.add(this.onDialogAssignQuest, this),
            this._hud.dialogSystem.signals.complete.add(this.onDialogCompleteQuest, this),
            this._hud.dialogSystem.signals.reward.add(this.onDialogRewardItem, this),

            this._hud.pauseButton.signals.click.add(this.onPauseButtonClick, this),
            this._hud.actionButton.signals.click.add(this.onActionButtonClick, this),
            this._hud.mapButton.signals.click.add(this.onMapButtonClick, this),
            this._hud.questButton.signals.click.add(this.onQuestButtonClick, this),
            this._hud.inventoryButton.signals.click.add(this.onInventoryButtonClick, this),
            this._hud.surrenderButton.signals.click.add(this.onSurrenderButtonClick, this),
        );
        if (this._hud.shopButton) {
            this.signalListeners.push(this._hud.shopButton.signals.click.add(this.onShopButtonClick, this));
        }
        
    }

    /**
     * @param {PIXI.InteractionEvent} e 
     */
    onMouseDown(e) {
        var touch = {
            id: e.data.identifier,
            pos: e.data.global.clone()
        };

        if (!this.hud.isPointIntersecting(touch.pos)) {   
            this._joystickTouch = touch;
        } 
    }

    /**
     * @param {PIXI.InteractionEvent} e 
     */
    onMouseMove(e) {
        if (this._joystickTouch) {
            if (this._joystickTouch.id === e.data.identifier) {
                this._joystickTouch.pos = e.data.global.clone();
            }
        }
    }

    /**
     * @param {PIXI.InteractionEvent} e 
    */
    onMouseUp(e) {
        if (this._joystickTouch) {
            if (this._joystickTouch.id === e.data.identifier) {
                this._joystickTouch = null;
            }
        }
    }

    onLevelLoaded() {
        this._levelLoaded = true;

        this.resetTutorial();

        const levelData = json(this._loadedLevel);
        this._world.loadLevel(levelData);

        this._world.camera.target.set(this._world.tilemap.width * 0.5, this._world.tilemap.height * 0.5);
        this._world.camera.zoom = this._cameraZoomInitial;
        
        this.playIncidentalMusic();

        if (levelData.ambientSounds) {
            for (let i = 0; i < levelData.ambientSounds.length; i++) {
                this._ambientSounds.push(P3.audio.playSound(levelData.ambientSounds[i], { loop: true }));
            }
        }

        if (this._randomSFX.length > 0) {
            this._randomSFXTimer.current = 0;
            this._randomSFXTimer.active = true;
            this._randomSFXTimer.limit = this._randomSFXTimer.limitRange[0] + ((this._randomSFXTimer.limitRange[1] - this._randomSFXTimer.limitRange[0]) * Math.random()); 
        }

        this.startLevel();

        const levelTitle = new LocationTitle(locale(levelData.metadata.name));
        this.addChild(levelTitle);
    }

    startLevel() {
        this._playerStart = this._levelSpawns[this._levelShortcutFrom];
       // //console.log(this._levelSpawns);

        this._ivandoe = new PlayerIvandoe();
        this._ivandoe.position.set(this._playerStart.x, this._playerStart.y);
        this._ivandoe.velocity.set(0);
        this._ivandoe.resetJoystick();
        this._ivandoe.signals.attack.add(this.onPlayerAttack, this);
        this._world.addEntity(this._ivandoe);

        this._bert = new PlayerBert();
        this._bert.position.set(this._ivandoe.x - 100, this._ivandoe.y);
        this._bert.velocity.set(0);
        this._bert.resetJoystick();
        this._world.addEntity(this._bert);

        this._world.camera.target.set(this._ivandoe.x, this._ivandoe.y - 90);

        this._bert.disableInput = true;
        this._bert.moveToTarget = this._ivandoe;

        // fires when virtual joystick starts
        this._ivandoe.signals.joystickStart.add(this._hud.onVirtualJoystickStart, this._hud);

        // fires when virtual joystick stops
        this._ivandoe.signals.joystickStop.add(this._hud.onVirtualJoystickStop, this._hud);

        this._questManager.init();

        this.setEnemyTarget(this._ivandoe);

        this.initTutorial();

        console.log(this._levelSpawns);
    }

    initTutorial() {
        if (this.isTutorialActive) {
            this._initialTutorialPosition = new PIXI.Point(this._ivandoe.x, this._ivandoe.y);
            this._tutorialControlsHidden = false;

            const guide = this.findNPC("tutorial_guide");
            if (guide) {
                this._hud.tutorialPointer.setDirection(this._world.camera, guide, new PIXI.Point(0, -100));
                this._hud.tutorialControls.setState(definitions.TUTORIAL_STATE.MOVEMENT, true);
            } else {
                this._hud.tutorialPointer.setDirection(this._world.camera, null);
            }
        } else {
            this._hud.tutorialControls.setState(definitions.TUTORIAL_STATE.NONE, false);
            this._hud.tutorialPointer.setDirection(this._world.camera, null);
        }

        if (this._loadedLevel === definitions.LEVEL_ID.KINGDOM_STAG) {
            const kristine = this.findNPC("kristine");
            if (kristine) {
                if (!this._questManager.isQuestActive("quest_1", kristine) && !this._questManager.isQuestComplete("quest_1", kristine)) {
                    this._hud.tutorialPointer.setDirection(this._world.camera, kristine, new PIXI.Point(0, -130));
                }
            }
        }
    }

    updateTutorial() {
        if (this.isTutorialActive) {
            if (!this._tutorialControlsHidden) {
                const guide = this.findNPC("tutorial_guide");
                if (guide) {
                    const to = new P3.Vector2(guide.x - this._ivandoe.x, guide.y - this._ivandoe.y);
                    if (to.lengthSq < guide.interactionRadius ** 2) {
                        this._hud.tutorialControls.setState(definitions.TUTORIAL_STATE.INTERACTION);
                    } else {
                        this._hud.tutorialControls.setState(definitions.TUTORIAL_STATE.MOVEMENT);
                    }

                    const localPos = this._hud.tutorialControls.toLocal(new PIXI.Point(guide.x, guide.y), guide.parent);
                    this._hud.tutorialControls.pointer.position.set(localPos.x, localPos.y - 60);
                }
            }
        }
    }

    resetTutorial() {
        if (this.isTutorialActive) {
            this._questManager.forgetQuest("tutorial_1", "badger");
            this._inventory.removeItem(definitions.ITEM_ID.DAISY_FLOWERS, Number.MAX_VALUE);
        }
    }

    completeTutorial() {
        if (this.isTutorialActive) {
            common.saveData.activeSlot.player.tutorial = false;
            common.saveData.saveGame();

            this._hud.tutorialPointer.setDirection(this._world.camera, null);

            this._hud.setButtonsInteractiveState(false);
            this.delay(2).then(() => {
                //this._hud.interactive = false; 
                this.loadLevel(definitions.LEVEL_ID.KINGDOM_STAG).then(() => {
                    //this._hud.interactive = true;
                    this._hud.setButtonsInteractiveState(true);
                });
            });
        }
    }

    resize() {
        super.resize();

        if (this.isSceneLoaded) {
            this._hud.resize();
            if (this._idleMessage) {
                this._idleMessage.x = (common.STAGE_WIDTH / 2) - (P3.View.width / 2);
            }
        }
    }

    update() {
        super.update();

        if (this.isSceneLoaded && this.isLevelLoaded) {
            this.updateCamera();
            this._world.update();
            this._hud.update();

            this.updateInteraction();
            this.updateMinigame();
            this.updateDebugShortcuts();
            this.updateTutorial();

            this.collectNearbyItems();

            const tile = this._world.tilemap.toTile(this._ivandoe.x, this._ivandoe.y);
            if (tile.x < 0) {
                this.useLevelShortcut(definitions.LEVEL_SHORTCUT.WEST);
            } else if (tile.x > this._world.tilemap.columns) {
                this.useLevelShortcut(definitions.LEVEL_SHORTCUT.EAST);
            } else if (tile.y < 0) {
                this.useLevelShortcut(definitions.LEVEL_SHORTCUT.NORTH);
            } else if (tile.y > this._world.tilemap.rows) {
                this.useLevelShortcut(definitions.LEVEL_SHORTCUT.SOUTH);
            }

            if (P3.input.isKeyPressed(P3.keys.SPACE)) {
                this.hud.actionButton.promptButton();
                this.performAction();
            }

            if (this._incidentalMusicDelay.current > 0 && !this.isMinigameActive && !this.isBossFightActive) {
                this._incidentalMusicDelay.current += window.dt;
                if (this._incidentalMusicDelay.current >= this._incidentalMusicDelay.max) {
                    this.playIncidentalMusic();
                    this._incidentalMusicDelay.current = 0;
                }
            }

            if (this._randomSFXTimer.active) {
                this._randomSFXTimer.current += window.dt;
                if(this._randomSFXTimer.current >= this._randomSFXTimer.limit) {
                    P3.audio.playSound(this._randomSFX);
                    this._randomSFXTimer.limit = this._randomSFXTimer.limitRange[0] + ((this._randomSFXTimer.limitRange[1] - this._randomSFXTimer.limitRange[0]) * Math.random());
                    this._randomSFXTimer.current = 0;
                }
            }

            if (this._activeMinigame == null && this._activeBossFight == null) {
                this._idleTime.current += window.dt;
                if (this._idleTime.current >= this._idleTime.maxTime) {
                    this._showIdleMessage();
                    this._idleTime.current = 0;
                }
            }
            
            if (this.isTutorialActive) {
                if (common.saveData.activeSlot.player.tutorial) {
                    this.hud.mapButton.alpha = 0.6;
                    this.hud.mapButton.enabled = false;
                } else {
                    this.hud.mapButton.alpha = 1;
                    this.hud.mapButton.enabled = true;
                }
            } 
        }
    }

    /**
     * @private
     */
    updateDebugShortcuts() {
        // delete save shortcut
        /*
        if (P3.input.isKeyDown(46)) {
            if (!this._debugDeleteSlot) {
                this._debugDeleteSlot = true;
                this._debugDeleteSlotTime = window.performance.now();
            } else if (window.performance.now() - this._debugDeleteSlotTime > 1000) {
                common.saveData.createEmptySlot(common.saveData.activeSlot.id, true);
                this._debugDeleteSlot = false;
                location.reload();
                alert("Save deleted");
            }
        } else {
            this._debugDeleteSlot = false;
        }
        */

        if (!common.config.debug) return;

        // teleport
        if (P3.input.isKeyDown(P3.keys.SHIFT)) {
            if (P3.input.isKeyPressed(P3.keys.LEFT)) {
                performNextTick(() => this.useLevelShortcut(definitions.LEVEL_SHORTCUT.WEST));
            }
            if (P3.input.isKeyPressed(P3.keys.RIGHT)) {
                performNextTick(() => this.useLevelShortcut(definitions.LEVEL_SHORTCUT.EAST));
            }
            if (P3.input.isKeyPressed(P3.keys.UP)) {
                performNextTick(() => this.useLevelShortcut(definitions.LEVEL_SHORTCUT.NORTH));
            }
            if (P3.input.isKeyPressed(P3.keys.DOWN)) {
                performNextTick(() => this.useLevelShortcut(definitions.LEVEL_SHORTCUT.SOUTH));
            }
        }

        // health cheats
        if (P3.input.isKeyDown(P3.keys.SHIFT)) {
            if (P3.input.isKeyPressed(P3.keys.MINUS)) {
                if (this._playerHearts > this._playerHeartsMin) {
                    this.setPlayerHearts(--this._playerHearts, true);
                }
            }
            if (P3.input.isKeyPressed(P3.keys.PLUS)) {
                if (this._playerHearts < this._playerHeartsMax) {
                    this.setPlayerHearts(++this._playerHearts, true);
                }
            }
        } else {
            if (P3.input.isKeyPressed(P3.keys.MINUS)) {
                this._playerHealth = Math.max(0, this._playerHealth - 1);
                this._hud.healthBar.health = this._playerHealth;

                this.playerTakeDamage();
            }
            if (P3.input.isKeyPressed(P3.keys.PLUS)) {
                this._playerHealth = Math.min(this._hud.healthBar.healthMax, this._playerHealth + 1);
                this._hud.healthBar.health = this._playerHealth;
            }
        }
    }

    updateCamera() {
        if (this._ivandoe) {
            this._cameraTarget.x = this._ivandoe.x + this._ivandoe.velocity.x * 0.6;
            this._cameraTarget.y = this._ivandoe.y - 90 + this._ivandoe.velocity.y * 0.6;

            if (!this._ivandoe.isDead) {
                this._cameraZoomTarget = this._cameraZoomInitial - this._ivandoe.velocity.length * 0.00032;
            }
        }

        this._world.camera.target.x += (this._cameraTarget.x - this._world.camera.target.x) * this._cameraTargetEase * window.dt;
        this._world.camera.target.y += (this._cameraTarget.y - this._world.camera.target.y) * this._cameraTargetEase * window.dt;
        this._world.camera.zoom += (this._cameraZoomTarget - this._world.camera.zoom) * this._cameraZoomTargetEase * window.dt;
    }

    _recordLocationVisit() {
        const lastLocation = common.saveData.activeSlot.player.lastLocation;
        
        if (lastLocation && lastLocation === this._loadedLevel) {
            return;
        } 

        common.saveData.activeSlot.player.lastLocation = this._loadedLevel;
        common.saveData.saveGame();
    }

    _showIdleMessage() {
        let message = 'ui_explore_continue';
        if (this._questManager.getActiveQuests().length > 0) {
            message = 'ui_quest_continue';
        } else if (this._questManager.isQuestComplete('quest_1', 'queen_kristine')) {
            return;
        }
        this._idleMessage = new IdleMessage(locale(message), () => {
            this.removeChild(this._idleMessage);
        });
        this._idleMessage.y = 150;
        this.addChild(this._idleMessage);
        this.resize();
    }

    /**
     * @param {string} name
     * @param {string} [shortcut=definition.LEVEL_SHORTCUT.DEFAULT]
     * @returns {Promise}
     */
    loadLevel(name, shortcut = definitions.LEVEL_SHORTCUT.DEFAULT) {
        this._loadedLevel = name;
        this._levelLoaded = false;
        this._levelShortcutFrom = shortcut;

        let manifest = this.manifest;

        const levelData = json(name);
        const files = objectToArray(levelData.files);
        for (let i = 0, count = files.length; i < count; ++i) {
            const file = files[i];
            file.metadata = Object.assign({}, { unload: definitions.SCENE_LOADER_ASSET.TEMPORARY }, files[i].metadata);
        }
        manifest = manifest.concat(files);

        const sounds = objectToArray(levelData.sounds);
        for (let i = 0, count = sounds.length; i < count; ++i) {
            const sound = sounds[i];
            sound.url = sound.url + P3.assets.audioFileExt;
            sound.metadata = Object.assign({}, { unload: definitions.SCENE_LOADER_ASSET.TEMPORARY }, sounds[i].metadata);
        }
        manifest = manifest.concat(sounds);

        this.unloadLevel();

        this._levelShortcuts = {}
        if (levelData.metadata.shortcuts) {
            this._levelShortcuts[definitions.LEVEL_SHORTCUT.NORTH] = levelData.metadata.shortcuts[definitions.LEVEL_SHORTCUT.NORTH];
            this._levelShortcuts[definitions.LEVEL_SHORTCUT.EAST] = levelData.metadata.shortcuts[definitions.LEVEL_SHORTCUT.EAST];
            this._levelShortcuts[definitions.LEVEL_SHORTCUT.SOUTH] = levelData.metadata.shortcuts[definitions.LEVEL_SHORTCUT.SOUTH];
            this._levelShortcuts[definitions.LEVEL_SHORTCUT.WEST] = levelData.metadata.shortcuts[definitions.LEVEL_SHORTCUT.WEST];
        }
        
        for (let i in this._levelShortcuts) {
            if (this._levelShortcuts[i] && json(this._levelShortcuts[i]).metadata.phase > settings.PHASE) {
                this._levelShortcuts[i] = undefined;
            }
        }

        this._actors.length = 0;
        this._enemies.length = 0;
        this._npcs.length = 0;
        this._items.length = 0;
        this._interactions.length = 0;
        this._ivandoe = null;
        this._bert = null;

        for(let i in this._ambientSounds) {
            this._ambientSounds[i].stop();
        }
        this._ambientSounds = [];

        if (levelData.randomSFX && levelData.randomSFX.length > 0) {
            this._randomSFX = levelData.randomSFX;
        } else {
            this._randomSFX = [];
        }
        this._randomSFXTimer.active = false;

        const currentTrack = this._incidentalMusicList.length > 0 ? this._incidentalMusicList[this._incidentalMusicTrack] : null;
        this._incidentalMusicList = shuffleArray(levelData.incidentalMusic ? levelData.incidentalMusic.slice() : []);
        if (currentTrack && this._incidentalMusicList.indexOf(currentTrack) === 0) {
            this._incidentalMusicList.push(this._incidentalMusicList.splice(0, 1)[0]);
        }
        this._incidentalMusicTrack = 0;

        this._recordLocationVisit();

        //return this.addSceneLoader(manifest, levelData.metadata.name.replace('\n', ' ')).then(() =>
        return this.addSceneLoader(manifest, locale(levelData.metadata.name).toUpperCase()).then(() =>
            performNextTick(() => this.onLevelLoaded()));
    }

    unloadLevel() {
        if (this._hud) {
            this._hud.dialogSystem.endDialog();
        }
        if (this._world) {
            this._world.unloadLevel();
        }
        this._loader.unload(true);
    }

    /**
     * @return {Promise}
     */
    resetLevel() {
        return this.loadLevel(this._loadedLevel, this._levelShortcutFrom).then(() => {
            performNextTick(() => {
                this.setPlayerHearts(this._playerHearts);
                this._ivandoe.reset();
            });
        });
    }

    /**
     * @param {string} direction
     */
    useLevelShortcut(direction) {
        const shortcut = this._levelShortcuts[direction];
        if (shortcut && !this.isMinigameActive) {
            this._ivandoe.signals.joystickStop.dispatch();
            this._ivandoe.resetJoystick();

            this.loadLevel(shortcut, direction).then();
          //  //console.log("[" + this.constructor.name + "]", "Using level shortcut (" + direction + ")")
        }
    }

    /**
     * @param {number} value
     * @param {boolean} [animate=false]
     */
    setPlayerHearts(value, animate = false) {
        this._playerHearts = clamp(value, this._playerHeartsMin, this._playerHeartsMax);
        this._hud.healthBar.setHearts(this._playerHearts, animate);
        this._playerHealth = this._hud.healthBar.health;
        common.saveData.activeSlot.player.hearts = this._playerHearts;
        common.saveData.saveGame();
    }

    /**
     * @param {number} gid
     * @param {object} data
     * @param {boolean} flippedHorizontally
     * @param {boolean} flippedVertically
     */
    filterEntity(gid, data, flippedHorizontally, flippedVertically) {
        const uid = Tilemap.getCustomProperty("uid", data.properties);
        const actor = Tilemap.getCustomProperty("actor", data.properties);
        if (actor) {
            this.createActor(actor, new PIXI.Point(data.x + data.width * 0.5, data.y), flippedHorizontally, uid);
            return false;
        }

        const npc = Tilemap.getCustomProperty("npc", data.properties);
        if (npc) {
            console.log(data.properties);
            
            const npcInstance = this.createNPC(npc, new PIXI.Point(data.x + data.width * 0.5, data.y), flippedHorizontally, uid);

            const jsonProps = json(data.properties[0].value);
            for (let i in jsonProps.quests) {
                if(QuestFactory.isMinigame(jsonProps.quests[i].type)) {
                    this._levelSpawns[data.properties[0].value] = new PIXI.Point(npcInstance.x + npcInstance.playerRespawnPos.x, npcInstance.y + npcInstance.playerRespawnPos.y);
                }
            }

            return false;
        }

        const item = Tilemap.getCustomProperty("item", data.properties);
        if (item) {
            const spawn = Tilemap.getCustomProperty("spawn", data.properties);
            this.createItem(item, new PIXI.Point(data.x + data.width * 0.5, data.y), flippedHorizontally, spawn, data.properties, uid);
            return false;
        }

        const enemy = Tilemap.getCustomProperty("enemy", data.properties);
        if (enemy) {
            if (!settings.WORLD_DISABLE_ENEMIES) {
                this.createEnemy(enemy, new PIXI.Point(data.x + data.width * 0.5, data.y), flippedHorizontally, true, data.properties, uid);
            }
            return false;
        }

        const spawn = Tilemap.getCustomProperty("player", data.properties);
        if (spawn) {
            this.createPlayerSpawn(spawn, new PIXI.Point(data.x + data.width * 0.5, data.y), data.properties);
            return false;
        }
        return true;
    }

    /**
     * @param {string} id
     * @param {PIXI.Point} position
     * @param {boolean} [flippedHorizontally=false]
     * @param {string} [uid=undefined]
     * @returns {Actor}
     */
    createActor(id, position, flippedHorizontally = false, uid = undefined) {
        const actor = ActorFactory.create(id);
        if (actor) {
            actor.uid = uid;
            actor.position = position;
            actor.scale.x = flippedHorizontally ? -1 : 1;

            this._world.addEntity(actor);
            this._actors.push(actor);
        }
        return actor;
    }

    /**
     * @param {string} id
     * @param {PIXI.Point} position
     * @param {boolean} [flippedHorizontally=false]
     * @param {string} [uid=undefined]
     * @returns {NPC}
     */
    createNPC(id, position, flippedHorizontally = false, uid = undefined) {
        const npc = NPCFactory.create(id);

        if (npc.metadata.character.phase) {
            if (npc.metadata.character.phase > settings.PHASE) {
                return;
            }
        }

        if (npc) {
            npc.uid = uid;
            npc.position = position;
            npc.scale.x = flippedHorizontally ? -1 : 1;

            npc.signals.interact.add((npc, source) => this.onNPCInteract(npc));

            this._world.addEntity(npc);
            this._npcs.push(npc);

            this.updateNPCStatusIndicator(npc);

            npc.enabled = this._questManager.isNPCExistConditionsMet(npc);
        }
        return npc;
    }

    /**
     * @param {string} id
     * @param {PIXI.Point} position
     * @param {boolean} [flippedHorizontally=false]
     * @param {boolean} [spawnOnInit=false]
     * @param {object} [properties={}]
     * @param {string} [uid=undefined]
     * @returns {Item}
     */
    createItem(id, position, flippedHorizontally = false, spawnOnInit = false, properties= {}, uid = undefined) {
        const item = ItemFactory.create(id, properties);
        item.signals.interact.add((item) => this.onItemInteract(item));
        item.signals.collect.add((item) => this.onItemCollect(item));
        this._items.push(item);

        if (item) {
            item.uid = uid;
            item.position = position;
            item.scale.x = flippedHorizontally ? -1 : 1;
            this._world.addEntity(item);

            spawnOnInit && item.spawn();
        }
        return item;
    }

    /**
     * @param {string} id
     * @param {PIXI.Point} position
     * @param {boolean} [flippedHorizontally=false]
     * @param {boolean} [canRespawn=false]
     * @param {object} [properties={}]
     * @param {string} [uid=undefined]
     * @returns {Enemy}
     */
    createEnemy(id, position, flippedHorizontally = false, canRespawn = false, properties= {}, uid = undefined) {
        let enemy = EnemyFactory.create(id, properties);
        this._enemies.push(enemy);
        
        if (enemy) {
            enemy.uid = uid;
            enemy.position = position;
            enemy.scale.x = flippedHorizontally ? -1 : 1;
            enemy.neighbours = this._enemies;
            enemy.canRespawn = canRespawn;
            enemy.signals.attack.add(this.onEnemyAttack, this);
            enemy.signals.dead.add(this.onEnemyDead, this);
            this._world.addEntity(enemy);
        }

        return enemy;
    }

    /**
     * @param {Enemy} enemy
     */
    removeEnemy(enemy) {
        const i = this._enemies.indexOf(enemy);
        if (i >= 0) {
            enemy.destroyNextFrame = true;
            this._enemies.splice(i, 1);
        }
    }

    removeAllEnemies() {
        for (let i = 0, count = this._enemies.length; i < count; ++i) {
            const enemy = this._enemies[i];
            enemy.destroyNextFrame = true;
        }
        this._enemies.length = 0;
    }

    /**
     * @param {string} spawn
     * @param {PIXI.Point} position
     * @param {object} [properties={}]
     */
    createPlayerSpawn(spawn, position, properties) {
        let shortcut = Tilemap.getCustomProperty("from", properties);
        if (shortcut) {
            this._levelSpawns[shortcut] = position;
        } else {
            this._levelSpawns[definitions.LEVEL_SHORTCUT.DEFAULT] = position;
        }
    }

    /**
     * @param {NPC|string} npc
     */
    updateNPCStatusIndicator(npc) {
        (typeof npc === "string") && (npc = this.findNPC(npc));
        if (npc) {
            // we hide the status if npc is not related to active minigame
            if (this.isMinigameActive) {
                if (npc.id !== this.activeMinigame.owner) {
                    npc.status.visible = false;
                    return;
                }
            }

            npc.status.visible = true;

            const questId = this._questManager.getCurrentQuestId(npc);
            if (questId) {
                if (this._questManager.isQuestActive(questId, npc) && !this._questManager.isQuestBossFight(questId, npc)) {
                    if (this._questManager.isQuestRequirementsMet(questId, npc)) {
                        npc.status.state = definitions.NPC_STATUS_INDICATOR_STATE.QUEST_REQUIREMENTS_MET;
                    } else {
                        npc.status.state = definitions.NPC_STATUS_INDICATOR_STATE.QUEST_IN_PROGRESS;
                    }
                } else {
                    if (this._questManager.isQuestMinigame(questId, npc)) {
                        npc.status.state = definitions.NPC_STATUS_INDICATOR_STATE.QUEST_MINIGAME;
                    } else if (this._questManager.isQuestBossFight(questId, npc)) {
                        npc.status.state = definitions.NPC_STATUS_INDICATOR_STATE.QUEST_BOSS_FIGHT;
                    } else {
                        npc.status.state = definitions.NPC_STATUS_INDICATOR_STATE.QUEST_AVAILABLE;
                    }
                }
            } else {
                npc.status.state = definitions.NPC_STATUS_INDICATOR_STATE.QUEST_NONE;
            }
        }
    }

    updateAllNPCStatusIndicators() {
        for (let i = 0, count = this._npcs.length; i < count; ++i) {
            const npc = this._npcs[i];
            this.updateNPCStatusIndicator(npc);
        }
    }

    /**
     * @param {string} id
     * @param {number} [count=1]
     * @returns {boolean}
     */
    spawnItems(id, count = 1) {
        const items = shuffleArray(this._items.filter(x => x.id === id && !x.isSpawned));

        const spawnCount = Math.min(count, items.length);
        for (let i = 0; i < spawnCount; ++i) {
            const item = items[i];
            item.spawn();
        }
        if (items.length < count) {
            console.warn("[" + this.constructor.name + "]",
                "Not enough items available for '" + id + "' (" + spawnCount + "/" + count + ")");
        }
    }

    /**
     * @param {Quest} quest
     */
    spawnItemsForQuest(quest) {
        if (this._questManager.isQuestActive(quest.id, quest.owner)) {
            if (quest instanceof QuestFetch) {
                const count = quest.target - this._inventory.itemQuantity(quest.item);
                this.spawnItems(quest.item, count);
            }
        }
    }

    /**
     * @param {Item} item
     * @returns {boolean}
     */
    collectItem(item) {
        const i = this._items.indexOf(item);
        if (i >= 0) {

            if(!item.collect()){
                return false;
            }

            const count = this._inventory.itemQuantity(item.id);
            !item.consumable && this._inventory.addItem(item.id);

          //  //console.log("collected item '" + item.id + "'");

            if (!item.consumable) {
                this._ivandoe.playGrabAnimation();
                if (count === 0) {
                    this._hud.showItemFound(item.texture).then();
                }
            }

            this._questManager.setProgressEach((quest) => {
                if (quest.item == item.id) {
                    this._idleTime.current = 0;
                    return this.onQuestProgressPredicate(quest, true);
                }
                return false;
            });

            if (Math.random() < 0.35) {
                P3.audio.playSound("vo_iv_ooh_00");
            }

            !item.consumable && this._ivandoe.collectItem(item).then((icon) => this._hud.animateToInventory(icon).then(() => {}));
            return true;
        }
        return false;
    }

    collectNearbyItems() {
        const items = this._items.filter(x => x.isSpawned);
        for (let i = 0, count = items.length; i < count; ++i) {
            const item = items[i];
            const to = new P3.Vector2(item.x - this._ivandoe.x, item.y - this._ivandoe.y);
            if (to.lengthSq < this._collectNearbyItemRange ** 2) {
                this.collectItem(item);
            }
        }
    }

    /**
     * @param {string} id
     * @return {Actor}
     */
    findActor(id) {
        return this.findActors(id)[0];
    }

    /**
     * @param {string} id
     * @return {Actor[]}
     */
    findActors(id) {
        const t = this._actors.filter(x => x.uid === id);
        return t.length > 0 ? t : this._actors.filter(x => x.id === id);
    }

    /**
     * @param {string} id
     * @return {NPC}
     */
    findNPC(id) {
        const t = this._npcs.filter(x => x.uid === id)[0];
        return t ? t : this._npcs.filter(x => x.id === id)[0];
    }

    /**
     * @param {string} id
     * @return {Enemy}
     */
    findEnemy(id) {
        return this.findEnemies(id)[0];
    }

    /**
     * @param {string} id
     * @return {Actor[]}
     */
    findEnemies(id) {
        const t = this._enemies.filter(x => x.uid === id);
        return t.length > 0 ? t : this._enemies.filter(x => x.id === id);
    }

    /**
     * @param {Enemy} enemy
     * @param {PIXI.Point} source
     * @param {number} damage
     * @param {number} pushback
     */
    damageEnemy(enemy, source, damage, pushback) {
        if (enemy.allowDamagePushback) {
            const force = new P3.Vector2(enemy.x - source.x, enemy.y - source.y);
            force.normalize();
            force.scaleBy(pushback);

            if (enemy.collisionDynamic) {
                enemy.velocity.x += force.x;
                enemy.velocity.y += force.y;
            }
        }
        enemy.takeDamage(damage);
    }

    /**
     * @param {Entity} entity
     */
    setEnemyTarget(entity) {
        for (let i = 0, count = this._enemies.length; i < count; ++i) {
            const enemy = this._enemies[i];
            enemy.target = entity;
        }
    }

    playIncidentalMusic() {
        if (common.saveData.settings.musicMute) {
            return;
        }
        this._incidentalMusic = P3.audio.playSound(this._incidentalMusicList[this._incidentalMusicTrack], {
            loop: false,
            callback: () => this.onIncidentalMusicComplete()
        });
    }

    pause() {
        if (this._incidentalMusic) {
            this._incidentalMusic.stop();
        }
        for (let i in this._ambientSounds) {
            this._ambientSounds[i].pause();
        }
       // //console.log('pause');
    }

    resume() {
        if (this._incidentalMusicDelay.current === 0) {
            this.playIncidentalMusic();
        }
        for (let i in this._ambientSounds) {
            this._ambientSounds[i].play();
        }
      //  //console.log('resume');
    }

    updateInteraction() {
        let actors = [];

        actors = actors.concat(this._actors.filter(x => x.interactable));
        actors = actors.concat(this._npcs.filter(x => x.interactable));
        actors = actors.concat(this._items.filter(x => x.interactable));
        actors = actors.concat(this._enemies.filter(x => x.interactable));

        this._interactions = [];

        for (let i = 0, count = actors.length; i < count; ++i) {
            const actor = actors[i];
            if (actor.enabled) {
                const to = new P3.Vector2(actor.x - this._ivandoe.x, actor.y - this._ivandoe.y);
                const distance = to.lengthSq;
                actor.__interactionDistance = distance;
                if (distance < actor.interactionRadius ** 2) {
                    if (!actor.inInteractionRange) {
                        actor.onInteractionRangeEnter();
                    }
                    actor.onInteractionRangeUpdate();
                    this._interactions.push(actor);
                } else {
                    if (actor.inInteractionRange) {
                        actor.onInteractionRangeExit();
                    }
                }
            }
        }

        // find closest interaction for action button
        this._interactions.sort((x, y) => {
            return x.__interactionDistance - y.__interactionDistance;
        });

        let action = definitions.PLAYER_ACTION.ATTACK; 
    
        const dialog = this._hud.dialogSystem;
        if (dialog.isDialogActive &&
                dialog.target instanceof NPCGiantChick ||
                dialog.target instanceof NPCGiantSpider ||
                dialog.target instanceof NPCBatwitch ||
                dialog.target instanceof NPCSuzie) 
        {
            this._interactions.push(dialog.target);
            action = definitions.PLAYER_ACTION.TALK;
        }

        if (this.interaction && this._ivandoe.stance === definitions.PLAYER_STANCE.NORMAL) 
        {
            if (this.interaction instanceof NPC) {
                action = definitions.PLAYER_ACTION.TALK;
            } 
            else if (this.interaction.interactable) {
                action = definitions.PLAYER_ACTION.INTERACT;
            }
        }

        this._hud.actionButton.setAction(action, true);
    }

    /**
     * @param {QuestMinigame} minigame
     * @private
     */
    _startMinigame(minigame) {
        if (!this.isMinigameActive) {
            this._activeMinigame = minigame;
            this._activeMinigameTime = 0;
            this._activeMinigameDuration = minigame.duration;
            minigame.onMinigameStart(this);
           // //console.log("[" + this.constructor.name + "]", "Minigame '" + minigame.id + "' started");

            this._hud.setButtonLayout(definitions.HUD_BUTTON_LAYOUT.MINIGAME);

            this._hud.minigameTracker.setName(minigame.name);
            this._hud.minigameTracker.setDescription(minigame.description);
            this._hud.minigameTracker.setTime(this._activeMinigameTime, this._activeMinigameDuration, false);
            this._hud.minigameTracker.animateIn().then();

            this.updateAllNPCStatusIndicators();

            if (this._incidentalMusic) {
                this._incidentalMusic.stop();
            }
        }
    }

    /**
     * @private
     */
    updateMinigame() {
        if (this._activeMinigame) {
            this._activeMinigame.onMinigameUpdate(this);

            this._hud.minigameTracker.setTime(this._activeMinigameTime, this._activeMinigameDuration, true);

            this._activeMinigameTime += window.dt;
            if (this._activeMinigameTime >= this._activeMinigameDuration) {
                this._endMinigame();
            }
        }
    }

    /**
     * @param {boolean} [cancel=false]
     * @private
     */
    _endMinigame(cancel= false) {
        if (this._activeMinigame) {
            const minigame = this._activeMinigame;
            this._activeMinigame = null;

            if (cancel) {
                minigame.onMinigameCancel(this);
              //  //console.log("[" + this.constructor.name + "]", "Minigame '" + minigame.id + "' cancelled");
            } else {
                minigame.onMinigameEnd(this);
              //  //console.log("[" + this.constructor.name + "]", "Minigame '" + minigame.id + "' ended");
            }

            this._hud.setButtonLayout(definitions.HUD_BUTTON_LAYOUT.DEFAULT);
            this._hud.minigameTracker.animateOut().then();

            this.updateAllNPCStatusIndicators();

            if (this._incidentalMusicDelay.current === 0) {
                this.playIncidentalMusic();
            }

            if (cancel) {
                this._questManager.unassignQuest(minigame.id, minigame.owner);
            } else {
                this._questManager.setProgressEach((quest) => {
                    if (quest === minigame) {
                        return minigame.target;
                    }
                    return false;
                });

                this._questManager.completeQuest(minigame.id, minigame.owner);

                this.updateAllNPCStatusIndicators();

                // show minigame result panel
                this.signals.minigame.dispatch(minigame);
            }
        }
    }

    /**
     * @param {boolean} [success=false]
     */
    completeMinigame(success) {
        const minigame = this._activeMinigame;
        this._endMinigame();
    }

    /**
     * @param {QuestBossFight} bossFight
     * @private
     */
    _startBossFight(bossFight) {
        if (!this.isBossFightActive) {
            this._activeBossFight = bossFight;
            bossFight.onFightStart();
           // //console.log("[" + this.constructor.name + "]", "Boss Fight '" + bossFight.id + "' started");

            this.signals.boss.dispatch(bossFight.boss);
        }
    }

    /**
     * @param {boolean} success
     * @private
     */
    _endBossFight(success = false) {
        if (this.isBossFightActive) {
            const fight = this._activeBossFight;
            this._activeBossFight = null;

            fight.onFightEnd(this, success);
            //console.log("[" + this.constructor.name + "]", "Boss Fight '" + fight.id + "' ended");

            this._hud.setButtonLayout(definitions.HUD_BUTTON_LAYOUT.DEFAULT);
            this._hud.minigameTracker.animateOut().then();

            if (success) {
                this._questManager.setProgressEach((quest) => {
                    if (quest === fight) {
                        return fight.target;
                    }
                    return false;
                });
                this._questManager.completeQuest(fight.id, fight.owner);
            }

            this.updateAllNPCStatusIndicators();

            if (!this._hud.dialogSystem.isDialogActive) {
                let path = fight.id;

                const npc = this.findNPC(fight.owner);
                const vars = {};

                if (success) {
                    path += ".victory"
                } else {
                    path += ".defeat"
                }

                // show dialog
                this._hud.dialogSystem.beginDialog(npc.dialog, npc, path, vars)
                return true;
            }
        }
    }

    /**
     * @param {boolean} [success=false]
     */
    completeBossFight(success) {
        const fight = this._activeBossFight;
        this._endBossFight(success);
    }

    /**
     * @param {NPC} npc
     * @return {boolean}
     */
    interactWithNPC(npc) {
        const dialogSystem = this._hud.dialogSystem;

        // we cannot interact with certain npcs during minigames
        if (this.isMinigameActive || this.isBossFightActive) {
            if (npc.id !== this.activeMinigame.owner) {
                return false;
            }
        }

        if (!dialogSystem.isDialogActive) {
            let path;

            // have we finished the whole questline?
            if (!this._questManager.isQuestlineFinished(npc)) {
                if (this._questManager.isQuestAvailable(npc)) {
                    const questId = this._questManager.getCurrentQuestId(npc);
                    path = questId;

                    const data = this._questManager.getQuestData(questId, npc);
                    //console.log(data);

                    TextFormat.addFormatVar(definitions.TEXT_FORMAT_VAR.TARGET, data.metadata.target);

                    // meet npc if you have not met them and quests are active or completed
                    if (npc.introduce) {
                        npc.introduce = this._questManager.getQuestActiveCount(npc) +
                            this._questManager.getQuestCompleteCount(npc) === 0;
                    }

                    // if quest has met requirements, show 'ready' dialog
                    if (this._questManager.isQuestRequirementsMet(questId, npc)) {
                        path += ".ready";

                        // if quest is minigame, pass score to inky
                        if (this._questManager.isQuestMinigame(questId, npc)) {
                            //vars["score"] = this._questManager.getMinigameScore(questId, npc);
                            const score = this._questManager.getMinigameScore(questId, npc);
                            TextFormat.addFormatVar(definitions.TEXT_FORMAT_VAR.SCORE, score);
                        }

                        // if quest is already active, show 'active' dialog
                    } else if (this._questManager.isQuestActive(questId, npc) && !this._questManager.isQuestBossFight(questId, npc)) {
                        path += ".active";

                        // if we have not met npc before, show 'intro' dialog
                    } else if (npc.introduce) {
                        path = "intro";

                        // if quest is minigame and is complete, show 'replay' dialog
                    } else if (this._questManager.isQuestMinigame(questId, npc) &&
                        this._questManager.isQuestComplete(questId, npc)) {

                        // pass best score to inky
                        //vars["score"] = this._questManager.getMinigameBestScore(questId, npc);
                        const score = this._questManager.getMinigameScore(questId, npc);
                        TextFormat.addFormatVar(definitions.TEXT_FORMAT_VAR.SCORE, score);

                        path = questId + ".replay";
                    }
                }
            } else {
                // we have finished the questline, show 'outro' dialog
                path = "outro";
            }

            // show dialog
            dialogSystem.beginDialog(npc.dialog, npc, path)
            return true;
        }
        return false;
    }

    /**
     * @param {ActionButton} [source=null]
     */
    performAction(source= null) {
        const target = this.interaction;
        if (target && this._ivandoe.stance === definitions.PLAYER_STANCE.NORMAL) {
            const dialog = this._hud.dialogSystem;
            if (dialog.isDialogActive && dialog.isDialogReady) {
                dialog.nextDialog();
            } else {
                if (target instanceof NPC && this.isMinigameActive) {
                    this._ivandoe.attack();
                    return;
                }
                target.onInteractWith(this._ivandoe);
            }
        } else {
            this._ivandoe.attack();
        }
    }

    /**
     * @param {boolean} value
     */
    toggleHostileEnemyLogic(value) {
        for (let i = 0, count = this._enemies.length; i < count; ++i) {
            const enemy = this._enemies[i];
            if (enemy.hostile) {
                enemy.updateFSM = value;
                enemy.disableKinematic = !value;
            }
        }
    }

    playerTakeDamage() {
        if (!this._ivandoe.isDead) {
            this._ivandoe.takeDamage(1);
            this._bert.takeDamage(1);

            if (this._playerHealth <= 0) {
                this.playerDead();
            }
        }
    }

    /**
     * @param {number} value
     */
    playerRestoreHealth(value) {
        value = Math.max(1, value);

        this._playerHealth = Math.min(this._hud.healthBar.healthMax, this._playerHealth + value);
        this._hud.healthBar.health = this._playerHealth;

        this._ivandoe.damageEffect(definitions.PLAYER_DAMAGE_EFFECT.SLOW);
        this._ivandoe.playEatAnimation();

        //console.log("restored health:", value);
    }

    playerDead() {

        if(settings.IMMORTAL) {
            return;
        }
        this._ivandoe.holdingObject = false;
        this._ivandoe.die();

        if (!common.saveData.settings.musicMute) {
            P3.audio.playSound("music_ivandoe_passout");
        }

        this._cameraZoomTarget = this._cameraZoomInitial + 0.08;

        this.delay(2).then(() => {
            if (!common.saveData.settings.musicMute) {
                P3.audio.playSound("music_ivandoe_respawns");
            }
            this.resetLevel();
        });

        if (this.isMinigameActive) {
            this._levelShortcutFrom = this._activeMinigame.owner;
            console.log('this._levelShortcutFrom:', this._levelShortcutFrom);
            this._endMinigame(true);
        }
    }

    assignQuestManually(id, npc) {
        //console.log('assignQuestManually', id, npc);
        this._questManager.assignQuest(id, npc);
    }

    completeQuestManually(id, npc) {
        this._questManager.completeQuest(id, npc);
    }

    /**
     * @param {NPC} npc
     */
    onNPCInteract(npc) {
        const to = new P3.Vector2(npc.x - this._ivandoe.x, npc.y - this._ivandoe.y);
        if (this.interactWithNPC(npc) && npc.turnToFacePlayer) {
            // turn to face player
            npc.scale.x = -Math.sign(to.x);
        }

        if (this.isTutorialActive) {
            this._tutorialControlsHidden = true;
            this._hud.tutorialControls.setState(definitions.TUTORIAL_STATE.NONE, true);
        }
        this._idleTime.current = 0;
    }

    /**
     * @param {Item} item
     */
    onItemInteract(item) { 
        performNextTick(() => this.collectItem(item));
    }

    /**
     * @param {Item} item
     */
    onItemCollect(item) {
        //console.log('COLLECT', item);

        if (item.id === definitions.ITEM_ID.HEALTH_TURNIP) {
            this.playerRestoreHealth(4);
            P3.audio.playSound("sfx_ivandoe_eat");
        }

        P3.audio.playSound("sfx_quest_object_collected");
    }

    /**
     * @param {string} path
     */
    onDialogBegin(path) {
        //console.log("dialog begin");

        if (this._ivandoe.joystickActive) {
            this._ivandoe.signals.joystickStop.dispatch();
            this._ivandoe.resetJoystick();
        } 

        this._ivandoe.disableInput = true;
        this._ivandoe.playTalkAnimation();

        this.toggleHostileEnemyLogic(false);
    }

    /**
     * @param {string} path
     */
    onDialogNext(path) {
        //console.log("dialog next");
    }

    /**
     * @param {string} path
     * @param {NPC} npc
     */
    onDialogEnd(path, npc) {
        //console.log("dialog end");

        if (!this._ivandoe.isQuest) {
            this._ivandoe.disableInput = false;
            this.toggleHostileEnemyLogic(true);
        }

        // intro is finished, we force new interact to start first quest
        if (path.indexOf("intro") >= 0) {
            this.interactWithNPC(npc);
        }
    }

    /**
     * @param {NPC} npc
     */
    onDialogIntroduceNPC(npc) {
        npc.introduce = false;
    }

    /**
     * @param {string} id
     * @param {NPC} npc
     */
    onDialogAssignQuest(id, npc) {
        //console.log(id, this._questManager.isQuestMinigame(id, npc));
        if (!this._questManager.isQuestMinigame(id, npc)) {
            this._ivandoe.disableInput = true;
            this._ivandoe.playQuestStartAnimation();

            this.delay(1.7).then(() => {
                if (!this._hud.dialogSystem.isDialogActive) {
                    this._ivandoe.disableInput = false;
                    this.toggleHostileEnemyLogic(true);
                }
            });

            P3.audio.playSound("vo_iv_accept_quest");
            if (!common.saveData.settings.musicMute) {
                P3.audio.playSound("music_major_quest_new_started");
            }
        }
        this._questManager.assignQuest(id, npc);
    }

    /**
     * @param {string} id
     * @param {NPC} npc
     */
    onDialogCompleteQuest(id, npc) {
        this._ivandoe.disableInput = true;
        this._ivandoe.playQuestFinishAnimation();

        /** check if the npc should reward Ivandoe with any coins */
        const quest = this._questManager.getQuestData(id, npc);
        if (quest.metadata.coinReward) {
            const coins = new CoinAnimation(quest.metadata.coinReward, this);
            coins.y = -npc.height / 2;
            npc.addChild(coins);
            common.saveData.activeSlot.coins += quest.metadata.coinReward;
            common.saveData.saveGame();
        }

        this.delay(1.5).then(() => {
            if (!this._hud.dialogSystem.isDialogActive) {
                this._ivandoe.disableInput = false;
                this.toggleHostileEnemyLogic(true);
            }
        });

        this._questManager.completeQuest(id, npc);
    }

    /**
     * @param {string} id
     * @param {NPC} npc
     */
    onDialogRewardItem(id, npc) {
        this._ivandoe.playQuestFinishAnimation();

        this._inventory.addItem(id);
        const temp = ItemFactory.create(id);
        this._ivandoe.collectItem(temp).then((icon) => this._hud.animateToInventory(icon));
        temp.destroy();

        this._questManager.setProgressEach((quest) => this.onQuestProgressPredicate(quest), false);
    }

    /**
     * @param {Quest} quest
     * @param {boolean} started
     */
    onQuestInit(quest, started) {
        this._questManager.setProgressEach((quest) => this.onQuestProgressPredicate(quest), true);

        quest.onQuestInit(this);

        this.spawnItemsForQuest(quest);
        this.updateNPCStatusIndicator(quest.owner);

        if (started) {
            if (quest instanceof QuestMinigame) {
                this._startMinigame(quest);
            } else if (quest instanceof QuestBossFight) {
                this._startBossFight(quest);
            } else {
                this._hud.questTracker.trackQuestAssign(quest);
            }

            if (this.isTutorialActive) {
                this._hud.tutorialPointer.setDirection(this._world.camera, null);
                this.createEnemy(definitions.ENEMY_ID.FLYING_SNAKE, this._playerStart)
                this.setEnemyTarget(this._ivandoe);
            }

            if (this._loadedLevel === definitions.LEVEL_ID.KINGDOM_STAG) {
                const kristine = this.findNPC("kristine");
                if (kristine) {
                    if (this._questManager.isQuestActive("quest_1", kristine) && !this._questManager.isQuestComplete("quest_1", kristine)) {
                        this._hud.tutorialPointer.setDirection(this._world.camera, null);
                    }
                }
            }
        }
    }

    /**
     * @param {Quest} quest
     * @param {number} progress
     * @param {number} fromProgress
     * @param {boolean} init
     */
    onQuestProgress(quest, progress, fromProgress, init) {
        //console.log(quest, progress, fromProgress, init);

        this.updateNPCStatusIndicator(quest.owner);

        if (!(quest instanceof QuestMinigame)) {
            if (!init) {
                quest.onQuestProgress(this, progress);
                this._hud.questTracker.trackQuestProgress(quest);
            }
        }

        if (this.isTutorialActive) {
            if (progress >= 1) {
                const guide = this.findNPC("tutorial_guide");
                if (guide) {
                    this._hud.tutorialPointer.setDirection(this._world.camera, guide, new PIXI.Point(0, -100));
                }
            }
        }
    }

    /**
     * @param {Quest} quest
     */
    onQuestComplete(quest) {
        if (quest instanceof QuestFetch) {
            this._inventory.removeItem(quest.item, quest.target);
        }

        this.updateNPCStatusIndicator(quest.owner);

        let questNPC = null;
        for (let i = 0, count = this._npcs.length; i < count; ++i) {
            const npc = this._npcs[i];
            if (npc.id !== quest.owner) {
                npc.enabled = this._questManager.isNPCExistConditionsMet(npc);
            } else {
                questNPC = npc;
            }
        }

        if (!(quest instanceof QuestMinigame)) {
            quest.onQuestComplete(this);
            this._hud.questTracker.trackQuestComplete(quest);
        }
        if (quest instanceof QuestBossFight && questNPC && questNPC.moveToDefeatedPosition) {
            questNPC.moveToDefeatedPosition();
        }

        this.completeTutorial();

        //console.log('playOutro?', quest);
        if (quest.playOutro) {
            //console.log('playing outro');
            this.signals.outro.dispatch();
        }
    }

    /**
     * @param {Quest} quest
     */
    onQuestUnassign(quest) {
        this.updateNPCStatusIndicator(quest.owner);
    }

    /**
     * @param {Quest} quest
     * @param {Boolean} inventoryCheck
     */
    onQuestProgressPredicate(quest, inventoryCheck = false) {
        if (quest instanceof QuestDummy) {
            return quest.target;
        }
        if (quest instanceof QuestFetch) {
            if(this._inventory.isAnInventoryItem(quest.item) || !inventoryCheck) {
                //console.log('This is an inventory item', this._inventory.itemQuantity(quest.item));
                return this._inventory.itemQuantity(quest.item);
            } else {
                return null;
            }
        }
        return false;
    }

    /**
     * @param {Quest} quest
     * @param {Enemy} enemy
     */
    onQuestProgressKillPredicate(quest, enemy) {
        if (quest instanceof QuestKill) {
            if (quest.enemy === enemy.id) {
                return quest.value + 1;
            }
        }
        return false;
    }

    /**
     * @param {Player} player
     * @param {number} damage
     */
    onPlayerAttack(player, damage) {
        let success = false;
        for (let i = 0, count = this._enemies.length; i < count; ++i) {
            const enemy = this._enemies[i];
            const to = new P3.Vector2(enemy.x - player.x, enemy.y - player.y);
            const distance = to.lengthSq;
            to.normalize();

            const dir = new P3.Vector2(player.facingDirection, 0);
            if (distance < player.attackRange ** 2 && to.dot(dir) > 0.3) {
                this.damageEnemy(enemy, player.position, damage, random(500, 650));
                if (!enemy.isDead && enemy.hostile) {
                    success = true;
                }
            }
        }
        if (success) {
            P3.audio.playSound("sfx_ivandoe_hit_enemy");
        } else {
            P3.audio.playSound(['sfx_action_sword_swipe_00', 'sfx_action_sword_swipe_01', 'sfx_action_sword_swipe_02', 'sfx_action_sword_swipe_03']);
        }
    }

    /**
     * @param {Enemy} enemy
     * @param {Entity} target
     * @param {number} damage
     */
    onEnemyAttack(enemy, target, damage) {
        this._playerHealth = Math.max(0, this._playerHealth - damage);
        this._hud.healthBar.health = this._playerHealth;

        P3.audio.playSound([
            "vo_iv_lose_life_00",
            "vo_iv_lose_life_01",
            "vo_iv_lose_life_02"
        ]);        

        this.playerTakeDamage();
    }

    /**
     * @param {Enemy} enemy
     */
    onEnemyDead(enemy) {
        this._questManager.setProgressEach((quest) => this.onQuestProgressKillPredicate(quest, enemy));

        if (!enemy.canRespawn) {

        }

        if (Math.random() < definitions.ENEMY_TURNIP_DROP_CHANCE) {
            const item = this.createItem(definitions.ITEM_ID.HEALTH_TURNIP, enemy.position, false, true);
        }
    }

    /**
     * @param {P3.Button} button
     */
    onPauseButtonClick(button) {
        this.signals.pause.dispatch();
        this.pause();
    }

    /**
     * @param {ActionButton} button
     */
    onActionButtonClick(button) {
        this.performAction(button);
    }

    /**
     * @param {P3.Button} button
     */
    onMapButtonClick(button) {
        this.signals.map.dispatch();
        this.pause();
    }

    /**
     * @param {P3.Button} button
     */
    onQuestButtonClick(button) {
        this.signals.quest.dispatch(this._questManager);
        this.pause();
    }

    /**
     * @param {P3.Button} button
     */
    onInventoryButtonClick(button) {
        this.signals.inventory.dispatch(this._inventory);
        this.pause();
    }

    /**
     * @param {P3.Button} button
     */
    onShopButtonClick(button) {
        this.signals.shop.dispatch();
        this.pause();
    }

    /**
     * @param button
     */
    onSurrenderButtonClick(button) {
        if (this.isMinigameActive) {
            this._endMinigame(true);
        }
    }

    onIncidentalMusicComplete() {
        this._incidentalMusicTrack ++;
        if( this._incidentalMusicTrack >= this._incidentalMusicList.length ) {
            this._incidentalMusicTrack = 0;
        }
        this._incidentalMusicDelay.current = window.dt;
        this._incidentalMusicDelay.max = 2 + (Math.random() * 2);
        this._incidentalMusic = null;
    }

    get joystickTouch() {
        return this._joystickTouch;
    }

    /**
     * @returns {string}
     */
    get loadedLevel() {
        return this._loadedLevel;
    }

    /**
     * @return {boolean}
     */
    get isLevelLoaded() {
        return this._levelLoaded;
    }

    /**
     * @returns {World}
     */
    get world() {
        return this._world;
    }

    /**
     * @return {HUD}
     */
    get hud() {
        return this._hud;
    }

    /**
     * @return {Inventory}
     */
    get inventory() {
        return this._inventory;
    }

    /**
     * @returns {QuestManager}
     */
    get questManager() {
        return this._questManager;
    }

    /**
     * @return {Player}
     */
    get player() {
        return this._ivandoe;
    }

    /**
     * @return {QuestMinigame}
     */
    get activeMinigame() {
        return this._activeMinigame;
    }

    /**
     * @return {boolean}
     */
    get isMinigameActive() {
        return !!this._activeMinigame;
    }

    /**
     * @returns {QuestBossFight}
     */
    get activeBossFight() {
        return this._activeBossFight;
    }

    /**
     * @returns {boolean}
     */
    get isBossFightActive() {
        return !!this._activeBossFight;
    }

    /**
     * @return {Actor}
     */
    get interaction() {
        if (this._interactions.length > 0) {
            return this._interactions[0];
        }
        return null;
    }

    /**
     * @return {boolean}
     */
    get isTutorialActive() {
        return this._loadedLevel === definitions.LEVEL_ID.TUTORIAL;
    }
}

/**
 * @static
 * @type {GameScene}
 */
GameScene.active = null;